import { BlotterDensity, BlotterTable, Card, Text, useBlotterObservables, useBlotterTable } from '@talos/kyoko';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import type { PreTradeResponseData } from '../../../redux/preTradeApi';
import { usePublicMarketsColumns } from './usePublicMarketsColumns';
import { parsePublicMarkets } from './utils';

/** Balham theme's viewport min-height causes empty space below the last row in autoHeight mode. */
const TightAutoHeightWrapper = styled.div`
  .ag-center-cols-viewport,
  .ag-body-viewport {
    min-height: unset !important;
  }
`;

interface PublicMarketsProps {
  scenario?: PreTradeResponseData;
}

export function PublicMarkets({ scenario }: PublicMarketsProps) {
  const theme = useTheme();

  const rows = useMemo(() => {
    if (!scenario?.Analytics) {
      return [];
    }
    return parsePublicMarkets(scenario.Analytics);
  }, [scenario?.Analytics]);

  const columns = usePublicMarketsColumns({ rows });

  const { dataObservable, errorObservable } = useBlotterObservables({ data: rows });

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    density: BlotterDensity.Compact,
    columns,
    rowID: 'id',
    gridOptions: {
      domLayout: 'autoHeight',
      autoSizeStrategy: {
        type: 'fitGridWidth',
      },
      getRowStyle: () => {
        return { background: theme.backgroundCard, borderBottom: 'none' };
      },
    },
  });

  if (rows.length === 0) {
    return (
      <Card title="Public Markets" data-testid="public-markets">
        <Text color="colorTextSubtle">No public markets data available</Text>
      </Card>
    );
  }

  return (
    <Card title="Public Markets" data-testid="public-markets" p="spacing12">
      <TightAutoHeightWrapper>
        <BlotterTable {...blotterTable} headerBackground={theme.backgroundCard} hideHeaderBorder />
      </TightAutoHeightWrapper>
    </Card>
  );
}
